import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Flex, Form, FormProps, Input, InputRef } from 'antd';
import React, { memo, useContext, useEffect, useRef } from 'react';
import { useMutation } from 'react-relay';

import { ItemFragment$data } from '../../graphql/__generated__/ItemFragment.graphql';
import CreateListItemMutation from '../../graphql/mutations/CreateListItemMutation';
import UpdateListItemMutation from '../../graphql/mutations/UpdateListItemMutation';
import useCommitItem from '../../hooks/useCommitItem';
import useDispatchSetterStore from '../../hooks/useDispatchSetterStore';
import useDynamicSelector from '../../hooks/useDynamicSelector';
import { SmxPropsContext } from '../../providers/propsProvider';
import { getSetterListItem, IListItemState } from '../../store/slices/listItemSlice';
import { ListItemType } from '../../types';
import { ParentRefType } from './CommonLoaderListItem';

const { TextArea } = Input;

interface IListItemForm {
    connectionId: string;
    parentRef?: React.RefObject<ParentRefType>;
}

const ListItemForm: React.FC<IListItemForm> = memo(({ connectionId, parentRef }) => {
    const { nameList, prefixStore, extraFields } = useContext(SmxPropsContext);
    const setterListItem = useDispatchSetterStore<IListItemState>(getSetterListItem(nameList + prefixStore));
    const [form] = Form.useForm();
    const [commitCreate, isInFlightCreate] = useMutation(CreateListItemMutation);
    const [commitUpdate, isInFlightUpdate] = useMutation(UpdateListItemMutation);
    const { getCommitItem } = useCommitItem();
    const currentEditItem = useDynamicSelector('listItem', 'currentEditItem');
    const editMode = useDynamicSelector('listItem', 'editMode');
    const typeList = useDynamicSelector('listItem', 'typeList');
    const inputRef = useRef<InputRef>(null);

    const resetFieldForm = () => {
        form?.setFieldsValue({ name: '', code: '', description: '' });
    };

    useEffect(() => {
        if (!currentEditItem) {
            resetFieldForm();
        } else {
            form?.setFieldsValue({ ...currentEditItem });
        }
    }, [currentEditItem]);

    useEffect(() => {
        setterListItem({ spinner: isInFlightUpdate || isInFlightCreate });
    }, [isInFlightUpdate, isInFlightCreate]);

    const onFinish: FormProps<Partial<ListItemType>>['onFinish'] = (values) => {
        const data: Partial<ListItemType> = {
            type: nameList,
            name: values.name,
            code: values.code,
            description: values.description,
            child: currentEditItem?.child || '0',
        };

        if (parentRef?.current && parentRef.current.hasOwnProperty('getCurrentItem')) {
            data.parent = parentRef.current.getCurrentItem().id.toString();
        }

        (async () => {
            try {
                if (typeList === 'remote') {
                    console.log(data);

                    if (currentEditItem) {
                        commitUpdate(getCommitItem(data as ItemFragment$data, connectionId, currentEditItem.id));
                    } else {
                        commitCreate(getCommitItem(data as ItemFragment$data, connectionId, undefined, resetFieldForm));
                    }
                }
            } catch (err) {
                console.error(err);
                setterListItem({
                    messageError: 'Error! Duplicate name',
                });
            }
        })();

        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const onFinishFailed: FormProps<Partial<ListItemType>>['onFinishFailed'] = (errorInfo) => {
        console.log('Failed:', errorInfo);
    };

    const showForm = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault();
        setterListItem({ editMode: 'adding' });
    };

    return (
        <Form
            form={form}
            name={`editorItem-${nameList}${prefixStore}`}
            style={{ width: '200' }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
        >
            <Flex vertical align={'center'} style={{ padding: '0 10px' }}>
                {!['viewing', 'deleting'].includes(editMode) && (
                    <>
                        <Form.Item name="name" rules={[{ required: true }]} style={{ width: '100%', marginBottom: 10 }}>
                            <Input
                                placeholder="Please enter item"
                                ref={inputRef}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        </Form.Item>
                        {extraFields?.includes('code') && (
                            <Form.Item name="code" style={{ width: '100%', marginBottom: 10 }}>
                                <Input
                                    placeholder="Please enter code"
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                />
                            </Form.Item>
                        )}
                        {extraFields?.includes('code') && (
                            <Form.Item name="description" style={{ width: '100%', marginBottom: 10 }}>
                                <TextArea
                                    placeholder="Please enter description"
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                />
                            </Form.Item>
                        )}
                    </>
                )}
                <Form.Item style={{ marginBottom: 5 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={currentEditItem ? <SaveOutlined /> : <PlusOutlined />}
                        onClick={!['viewing', 'deleting'].includes(editMode) ? undefined : showForm}
                    >
                        {currentEditItem ? 'Save' : 'Add item'}
                    </Button>
                </Form.Item>
            </Flex>
        </Form>
    );
});

export default ListItemForm;
